import { AppointmentType as BeffeAppointmentType } from '@src/lib/__generated/beffe/sdk';
import { AppointmentName, OutsideAppointmentName } from '@src/components/ReasonForVisit/ReasonForVisit.helpers';

const SICK_TYPE_NAMES = new Set<string>([AppointmentName.Sick, AppointmentName.Urgent]);

/**
 * Maps a single Vetspire appointment type name to the corresponding BEFFE AppointmentType(s).
 *
 * - Sick / Urgent → Sick
 * - Tech Appt → TechAppt
 * - Vaccine Booster → VaccineBooster
 * - Grooming → Grooming
 * - Everything else (Preventative Care, etc.) → RoutineCheckup
 */
export function mapAppointmentNameToBeffeTypes(name: string): BeffeAppointmentType[] {
	if (SICK_TYPE_NAMES.has(name)) return [BeffeAppointmentType.Sick];
	if (name === OutsideAppointmentName.TechAppt) return [BeffeAppointmentType.TechAppt];
	if (name === OutsideAppointmentName.VaccineBooster) return [BeffeAppointmentType.VaccineBooster];
	if (name === OutsideAppointmentName.Grooming) return [BeffeAppointmentType.Grooming];
	return [BeffeAppointmentType.RoutineCheckup];
}

/**
 * Maps a PAO `visitReason` URL parameter value to BEFFE AppointmentType(s).
 * Returns `undefined` when the value is unrecognised so callers can apply their own default.
 */
export function mapVisitReasonToBeffeTypes(visitReason: string | null | undefined): BeffeAppointmentType[] | undefined {
	if (visitReason === 'sick') return [BeffeAppointmentType.Sick];
	if (visitReason === 'checkup') return [BeffeAppointmentType.RoutineCheckup];
	return undefined;
}

/**
 * Converts an array of Vetspire appointment types (with a `name` field) into a deduplicated
 * array of BEFFE AppointmentType enum values. Falls back to both types when the input is empty.
 */
export function mapVetspireTypesToBeffeTypes(vetspireTypes: { name?: string | null }[]): BeffeAppointmentType[] {
	const result = new Set<BeffeAppointmentType>();
	for (const apptType of vetspireTypes) {
		for (const beffeType of mapAppointmentNameToBeffeTypes(apptType.name ?? '')) {
			result.add(beffeType);
		}
	}
	return result.size > 0 ? [...result] : [BeffeAppointmentType.Sick, BeffeAppointmentType.RoutineCheckup];
}
